import { FileText, MapPin, PackageCheck, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import api, { getError } from "@/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { PdfFileActions } from "@/components/PdfFileActions";

const movementLabels = {
  entree: "Entrée",
  sortie: "Sortie",
  transfert: "Transfert",
  initial: "Stock initial",
};

const formatMoney = (value) => `${Number(value || 0).toLocaleString("fr-FR")} GNF`;

export const MovementDetailSheet = ({ movementId, onOpenChange }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!movementId) {
      setDetail(null);
      setError("");
      return;
    }

    api.get(`/movements/${movementId}`)
      .then((response) => {
        setDetail(response.data);
        setError("");
      })
      .catch((requestError) => setError(getError(requestError)));
  }, [movementId]);

  const movement = detail?.movement;
  const document = detail?.document;
  const totalQuantity = movement?.lines.reduce(
    (sum, line) => sum + Number(line.quantity || 0),
    0,
  ) || 0;
  const totalTransport = movement?.lines.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.transport_cost || 0),
    0,
  ) || 0;

  const getPdfBlob = async () => {
    const response = await api.get(`/documents/${document.document_id}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };

  return (
    <Sheet onOpenChange={onOpenChange} open={Boolean(movementId)}>
      <SheetContent className="detail-sheet-content" data-testid="movement-detail-sheet" side="right">
        {!movement && <SheetTitle className="sr-only">Détails du mouvement</SheetTitle>}
        {!movement && <SheetDescription className="sr-only">Chargement des détails du mouvement</SheetDescription>}
        {error && <p className="detail-sheet-error" data-testid="movement-detail-error">{error}</p>}
        {!movement && !error && <div className="loading-line" data-testid="movement-detail-loading">Chargement…</div>}
        {movement && (
          <>
            <SheetHeader className="detail-sheet-header">
              <div>
                <p className="eyebrow">MOUVEMENT {movement.movement_id}</p>
                <SheetTitle>{movementLabels[movement.movement_type] || "Mouvement"}</SheetTitle>
                <SheetDescription>
                  {new Date(movement.movement_date || movement.created_at).toLocaleString("fr-FR")}
                </SheetDescription>
              </div>
              {document && (
                <PdfFileActions
                  fileName={`${document.document_number}.pdf`}
                  getPdfBlob={getPdfBlob}
                  testIdPrefix="movement-detail"
                />
              )}
            </SheetHeader>
            <div className="detail-sheet-scroll">
              <section className="detail-metadata-grid" data-testid="movement-detail-summary">
                <article>
                  <MapPin size={17} />
                  <span>Dépôt source</span>
                  <strong>{movement.source_name}</strong>
                </article>
                <article>
                  <PackageCheck size={17} />
                  <span>Produits</span>
                  <strong>{movement.lines.length} ligne(s) · {totalQuantity}</strong>
                </article>
                <article>
                  <UserRound size={17} />
                  <span>Opérateur</span>
                  <strong>{movement.author_name}</strong>
                </article>
                <article>
                  <FileText size={17} />
                  <span>Transport total</span>
                  <strong>{formatMoney(totalTransport)}</strong>
                </article>
              </section>
              {movement.destination_name && (
                <section className="detail-section" data-testid="movement-destination-detail">
                  <p className="eyebrow">DESTINATION</p>
                  <strong>{movement.destination_name}</strong>
                </section>
              )}
              <section className="detail-section" data-testid="movement-lines-detail">
                <div className="detail-section-heading">
                  <div>
                    <p className="eyebrow">LIGNES DU MOUVEMENT</p>
                    <h3>Produits concernés</h3>
                  </div>
                </div>
                <div className="detail-lines-list">
                  {movement.lines.map((line, index) => {
                    const transportTotal = Number(line.quantity || 0) * Number(line.transport_cost || 0);

                    return (
                      <div data-testid={`movement-detail-line-${index}`} key={`${line.product_id}-${index}`}>
                        <strong>{line.product_name}</strong>
                        <span>Quantité : {line.quantity}</span>
                        <span>Transport unitaire : {formatMoney(line.transport_cost)}</span>
                        <b>Transport total : {formatMoney(transportTotal)}</b>
                      </div>
                    );
                  })}
                </div>
              </section>
              <section className="detail-section" data-testid="movement-notes-detail">
                <p className="eyebrow">OBSERVATION</p>
                <p>{movement.notes || "Aucune observation associée."}</p>
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};